import React from 'react'
import { styled } from '@mui/material/styles'
import { Box, Button, CircularProgress, TextField, Typography } from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import SecurityIcon from '@mui/icons-material/Security'
import logo from '@assembly/assets/logo.png'
import { getOneTimeCode, teamMemberLogin, getSSOAuthorizeUrl } from '@assembly/api/auth'
import { RoutePaths } from '@assembly/constants'
import validator from 'validator'
import LoadingButton from '@mui/lab/LoadingButton/LoadingButton'
import { MuiOtpInput } from 'mui-one-time-password-input'
import { useSnackbars } from '@assembly/hooks'
import { SnackbarType } from '@assembly/contexts'

interface LoginProps {
  onLoginSuccess?: () => void
}

const GoogleG = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" style={{ verticalAlign: 'middle', marginRight: 8 }}>
    <path fill="#4285F4" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.18 17.74 9.5 24 9.5z" />
    <path fill="#34A853" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.16 7.09-10.27 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
    <path fill="#EA4335" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.21-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
  </svg>
)

const Container = styled(Box)(() => ({
  height: '100vh',
  display: 'flex',
}))

const BoxLeft = styled(Box)(({ theme }) => ({
  backgroundColor: '#fff',
  width: '50%',
  display: 'flex',
  justifyContent: 'center',
  [theme.breakpoints.down('md')]: {
    width: '100%',
  },
}))

const BoxRight = styled(Box)(({ theme }) => ({
  background:
    'linear-gradient(90deg, hsla(235, 55%, 55%, 1) 0%, hsla(290, 25%, 76%, 1) 91%)',
  width: '50%',
  [theme.breakpoints.down('md')]: {
    display: 'none',
  },
}))

const Title = styled(Typography)(() => ({
  fontFamily: 'Space Grotesk Variable',
  fontSize: '40px',
  fontWeight: 500,
}))

const Description = styled(Typography)(() => ({
  fontFamily: 'Space Grotesk Variable',
  fontSize: '18px',
  marginTop: '5px',
  fontWeight: 400,
}))

const EmailInput = styled(TextField)(() => ({
  marginTop: '48px',
  fieldset: {
    borderRadius: '10px',
  },
  input: {
    fontFamily: 'Space Grotesk Variable',
  },
}))

const BoxLeftInnerContianer = styled(Box)(({ theme }) => ({
  width: '70%',
  [theme.breakpoints.up('xl')]: {
    width: '540px',
  },
  [theme.breakpoints.down('md')]: {
    width: '90%',
  },
  [theme.breakpoints.down('sm')]: {
    width: '95%',
  },
}))

const ACTION_BUTTON_HEIGHT = 48

const ActionButton = styled(LoadingButton)(({ theme }) => ({
  height: '60px',
  width: '100%',
  borderRadius: '35px',
  fontFamily: 'Lato',
  letterSpacing: '1px',
  fontSize: '16px',
  fontWeight: 400,
  marginTop: '30px',
  boxShadow: '0px 30px 40px -20px #4F59CC99',
  ':hover': {
    boxShadow: '0px 30px 40px -20px #4F59CC99',
  },
  [theme.breakpoints.down('lg')]: {
    fontSize: '14px',
  },
}))

const ArrowIcon = styled(ArrowForwardIcon)(() => ({
  marginLeft: '5px',
}))

const Logo = styled('img')(() => ({
  width: '211px',
}))

const Header = styled(Box)(() => ({
  height: '25%',
  marginTop: '25px',
}))

const OtpInputBox = styled(Box)(() => ({
  marginTop: '48px',
}))

const OtpInputLoadingBox = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'center',
  marginTop: '40px',
}))

const OtpErrorText = styled(Typography)(() => ({
  color: 'red',
  marginTop: '15px',
  textAlign: 'center',
}))

export default function Login({ onLoginSuccess }: LoginProps) {
  const [email, setEmail] = React.useState<string>('')
  const [invalid_email, setInvalid_email] = React.useState<boolean>(false)
  const [isLoading, setIsLoading] = React.useState<boolean>(false)
  const [isSSOLoading, setIsSSOLoading] = React.useState<boolean>(false)
  const [otp, setOtp] = React.useState('')
  const [isOtpInputVisible, setIsOtpInputVisible] =
    React.useState<boolean>(false)
  const [otpError, setOtpError] = React.useState<string>('')
  const [showSSOEmailStep, setShowSSOEmailStep] = React.useState<boolean>(false)
  const [ssoWorkEmail, setSsoWorkEmail] = React.useState<string>('')
  const { addAlert } = useSnackbars()

  const handleSignInWithSSO = async (provider: string) => {
    if (provider === 'saml') {
      setShowSSOEmailStep(true)
      setSsoWorkEmail('')
      return
    }
    setIsSSOLoading(true)
    try {
      const state = crypto.randomUUID?.() ?? Math.random().toString(36).slice(2)
      const redirectUri = `${window.location.origin}${RoutePaths.MainSSOCallback}`
      const url = await getSSOAuthorizeUrl({ provider, state, redirectUri })
      window.location.href = url
    } catch (err: any) {
      setIsSSOLoading(false)
      addAlert({
        message: err?.response?.data?.message || err?.message || 'Sign-in failed',
        type: SnackbarType.Error,
      })
    }
  }

  const handleSSOEmailBack = () => {
    setShowSSOEmailStep(false)
    setSsoWorkEmail('')
  }

  const handleSSOEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const emailVal = ssoWorkEmail.trim().toLowerCase()
    if (!emailVal || !emailVal.includes('@')) {
      addAlert({ message: 'Please enter a valid work email address.', type: SnackbarType.Error })
      return
    }
    const domain = emailVal.split('@')[1]
    if (!domain) {
      addAlert({ message: 'Please enter a valid work email address.', type: SnackbarType.Error })
      return
    }
    setIsSSOLoading(true)
    try {
      const state = crypto.randomUUID?.() ?? Math.random().toString(36).slice(2)
      const redirectUri = `${window.location.origin}${RoutePaths.MainSSOCallback}`
      const url = await getSSOAuthorizeUrl({ provider: 'saml', state, redirectUri, email: emailVal, domain })
      window.location.href = url
    } catch (err: any) {
      setIsSSOLoading(false)
      addAlert({
        message: err?.response?.data?.message || err?.message || 'Sign-in failed',
        type: SnackbarType.Error,
      })
    }
  }

  const handleChangeEmail = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value)
    setInvalid_email(false)
  }

  const handleClickLogin = async () => {
    if (email.length === 0 || !validator.isEmail(email)) {
      setInvalid_email(true)
      return
    }

    try {
      const params = new URLSearchParams()
      params.append('email', email)
      setIsLoading(true)
      await getOneTimeCode(params)
      setIsOtpInputVisible(true)
      setIsLoading(false)
    } catch (error: any) {
      setIsLoading(false)
      if (
        error.response &&
        error.response.data &&
        error.response.data.message &&
        typeof error.response.data.message === 'string'
      ) {
        addAlert({
          message: error.response.data.message,
          type: SnackbarType.Error,
        })
      }
    }
  }

  const submitOtp = async (otp: string) => {
    try {
      setIsLoading(true)
      await teamMemberLogin({ email, code: otp })
      if (onLoginSuccess) {
        onLoginSuccess()
      } else {
        window.location.reload()
      }
      setIsLoading(false)
    } catch (error: any) {
      setIsLoading(false)
      if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        setOtpError(error.response.data.message)
      }
    }
  }

  const handleChangeOtp = (newValue: string) => {
    setOtp(newValue)
    setOtpError('')
  }

  const validateChar = (value: string) => {
    return !value || isDigit(value)
  }

  function isDigit(value: any) {
    return /^\d$/.test(value)
  }

  const handleOtpComplete = (value: string) => {
    submitOtp(value)
  }

  return (
    <Container>
      <BoxLeft>
        <BoxLeftInnerContianer>
          <Header>
            <Logo src={logo} alt="logo" />
          </Header>
          <Title>
            {showSSOEmailStep ? 'Sign in with SSO' : isOtpInputVisible ? 'Enter OTP' : 'Internal Admin'}
          </Title>
          <Description>
            {showSSOEmailStep ? (
              'Enter your work email to continue with SSO'
            ) : isOtpInputVisible ? (
              <>
                Please check your email{' '}
                <Typography component="span" color="primary">
                  {email}
                </Typography>
                . We've sent you a one time code.
              </>
            ) : (
              'Hiring the Top 1% Talent to get your most critical operational work done.'
            )}
          </Description>
          {showSSOEmailStep ? (
            <Box component="form" onSubmit={handleSSOEmailSubmit} sx={{ mt: 4 }}>
              <EmailInput
                type="email"
                variant="outlined"
                label="Work email"
                placeholder="you@yourcompany.com"
                fullWidth
                value={ssoWorkEmail}
                onChange={(e) => setSsoWorkEmail(e.target.value)}
                disabled={isSSOLoading}
                required
                sx={{ mb: 4 }}
              />
              <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                <Button
                  fullWidth
                  variant="outlined"
                  size="large"
                  startIcon={<ArrowBackIcon />}
                  onClick={handleSSOEmailBack}
                  disabled={isSSOLoading}
                  sx={{ textTransform: 'none', height: ACTION_BUTTON_HEIGHT }}
                >
                  Back
                </Button>
                <LoadingButton
                  fullWidth
                  variant="contained"
                  size="large"
                  loading={isSSOLoading}
                  type="submit"
                  startIcon={<SecurityIcon />}
                  sx={{ borderRadius: '35px', height: ACTION_BUTTON_HEIGHT, textTransform: 'none' }}
                >
                  Continue with SSO
                </LoadingButton>
              </Box>
            </Box>
          ) : isOtpInputVisible ? (
            <OtpInputBox>
              <MuiOtpInput
                value={otp}
                onChange={handleChangeOtp}
                onComplete={handleOtpComplete}
                length={7}
                validateChar={validateChar}
                TextFieldsProps={{
                  inputProps: {
                    style: { fontSize: '25px', fontFamily: 'Lato' },
                  },
                  placeholder: '-',
                  disabled: isLoading,
                }}
              />
              {isLoading && (
                <OtpInputLoadingBox>
                  <CircularProgress />
                </OtpInputLoadingBox>
              )}
              {otpError && <OtpErrorText>{otpError}</OtpErrorText>}
            </OtpInputBox>
          ) : (
            <>
              <Button
                fullWidth
                variant="outlined"
                size="large"
                startIcon={<SecurityIcon />}
                onClick={() => handleSignInWithSSO('saml')}
                disabled={isSSOLoading}
                sx={{ mt: 4, mb: 2, textTransform: 'none', height: ACTION_BUTTON_HEIGHT }}
              >
                {isSSOLoading ? 'Signing in...' : 'Sign in with SSO'}
              </Button>
              <Button
                fullWidth
                variant="outlined"
                size="large"
                startIcon={<GoogleG />}
                onClick={() => handleSignInWithSSO('google')}
                disabled={isSSOLoading}
                sx={{ mb: 3, textTransform: 'none', height: ACTION_BUTTON_HEIGHT }}
              >
                Sign in with Google
              </Button>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  mt: 2,
                  mb: 2,
                }}
              >
                <Box sx={{ flex: 1, borderBottom: 1, borderColor: 'divider' }} />
                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                  or continue with email
                </Typography>
                <Box sx={{ flex: 1, borderBottom: 1, borderColor: 'divider' }} />
              </Box>
              <EmailInput
                type="email"
                variant="outlined"
                label="Email Address"
                fullWidth
                onChange={handleChangeEmail}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleClickLogin()
                  }
                }}
                value={email}
                error={invalid_email}
                helperText={
                  invalid_email
                    ? email.length === 0
                      ? 'Please enter email address'
                      : 'Invalid email address'
                    : null
                }
              />
            </>
          )}
          {!isOtpInputVisible && !showSSOEmailStep && (
            <ActionButton
              variant="contained"
              onClick={handleClickLogin}
              loading={isLoading}
            >
              SEND CODE TO EMAIL TO LOGIN
              <ArrowIcon />
            </ActionButton>
          )}
        </BoxLeftInnerContianer>
      </BoxLeft>
      <BoxRight></BoxRight>
    </Container>
  )
}
